// Mileage: a period's deductible value, distance by month (or day), totals by category and the
// rates behind them, worked out with the app's rules.

import { useMemo, useState } from "react";
import { CategoryBadge, chartStyle } from "../components/Category.tsx";
import { FilterSelect, Notice, type Option, Segmented } from "../components/Controls.tsx";
import { Icon } from "../components/Icon.tsx";
import { useAccountData } from "../data/AccountData.tsx";
import { currencyOf, displayUnit } from "../data/derived.ts";
import type { Category } from "../domain/categories.ts";
import { formatCount, formatDistance, formatMoney, formatRate, formatShortDate, inUnit, listText, plural, unitSymbol } from "../domain/format.ts";
import { type Interval, type PeriodKind, customPeriod, elapsedInterval, periodInterval, periodTitle, rangeText } from "../domain/periods.ts";
import { type DistanceUnit, compareStarts } from "../domain/rates.ts";
import { type Bucket, MileageSummary, bucketTotal } from "../domain/summary.ts";
import { navigate } from "../lib/router.ts";
import { type LogChoice, buildReport, downloadCSV, openReport } from "./exportActions.ts";

export type MileagePeriodChoice = Exclude<PeriodKind, "all">;

export const mileagePeriodOptions: Option<MileagePeriodChoice>[] = [
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "lastQuarter", label: "Last Quarter" },
  { value: "thisYear", label: "This Year" },
  { value: "lastYear", label: "Last Year" },
  { value: "custom", label: "Custom…" },
];

export function useMileagePeriod(initial: MileagePeriodChoice) {
  const now = useMemo(() => new Date(), []);
  const [kind, setKind] = useState<MileagePeriodChoice>(initial);
  const [from, setFrom] = useState(() => toInput(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(() => toInput(now));
  const period = kind === "custom" ? customPeriod(fromInput(from) ?? now, fromInput(to) ?? now) : { kind };
  const interval = periodInterval(period, now)!;
  const isRunning = interval.end > now && interval.start <= now;
  const subtitle = kind === "custom"
    ? rangeText(interval)
    : `${periodTitle(period)} · ${rangeText(isRunning ? elapsedInterval(interval, now) : interval)}`;
  return { now, kind, setKind, from, setFrom, to, setTo, period, interval, subtitle };
}

function toInput(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function fromInput(text: string): Date | undefined {
  const [year, month, day] = text.split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day) : undefined;
}

export function CustomDates({ from, to, setFrom, setTo }: { from: string; to: string; setFrom: (value: string) => void; setTo: (value: string) => void }) {
  return (
    <span className="cv-dates">
      <input type="date" aria-label="From" value={from} onChange={(event) => setFrom(event.target.value)} />
      <input type="date" aria-label="Through" value={to} onChange={(event) => setTo(event.target.value)} />
    </span>
  );
}

export function Mileage() {
  const data = useAccountData();
  const choice = useMileagePeriod("thisYear");
  const [vehicleId, setVehicleId] = useState("all");
  const unit = displayUnit(data, vehicleId === "all" ? undefined : vehicleId);
  const currency = currencyOf(data);

  const trips = vehicleId === "all" ? data.trips : data.trips.filter((trip) => trip.vehicleId?.toUpperCase() === vehicleId.toUpperCase());
  const summary = new MileageSummary(trips, data.catalog, data.rates, choice.interval, choice.now);
  const vehicleOptions: Option<string>[] = [{ value: "all", label: "All vehicles" }, ...data.vehicles.map((vehicle) => ({ value: vehicle.id, label: vehicle.name }))];
  const chartCategories = data.catalog.chartOrder.filter((category) => summary.categories.some((total) => total.category.rawValue === category.rawValue));
  const classified = summary.tripCount - summary.unclassifiedCount;
  const begunMonths = summary.months.filter((month) => !month.isFuture).length;
  const logChoice: LogChoice = {
    periodKind: choice.kind,
    from: choice.from,
    to: choice.to,
    vehicleId,
    categories: data.catalog.deductible.map((category) => category.rawValue),
  };

  return (
    <>
      <div className="cv-head">
        <div>
          <h1 className="cv-title">Mileage</h1>
          <p className="cv-sub">{choice.subtitle}</p>
        </div>
        <div className="cv-head-end">
          <Segmented label="Period" value={choice.kind} options={mileagePeriodOptions} onChange={choice.setKind} />
          {choice.kind === "custom" && <CustomDates from={choice.from} to={choice.to} setFrom={choice.setFrom} setTo={choice.setTo} />}
          {data.vehicles.length > 1 && <FilterSelect label="Vehicle" value={vehicleId} options={vehicleOptions} onChange={setVehicleId} />}
        </div>
      </div>

      <div className="cv-split">
        <div className="cv-list">
          {summary.unclassifiedCount > 0 && (
            <Notice action={<button className="cv-btn cv-btn-secondary" type="button" onClick={() => navigate("/trips?review=1")}>Classify</button>}>
              <b>{plural(summary.unclassifiedCount, "trip")} not classified</b>
              <div className="cv-hint">{formatDistance(summary.unclassifiedMeters, unit)} left out of these totals</div>
            </Notice>
          )}

          <section className="cv-card cv-card-pad" aria-labelledby="ded-label">
            <div id="ded-label" className="cv-hero-label">Deductible value</div>
            <div className="cv-hero-val">{formatMoney(summary.totalValue, currency)}</div>
            <div className="cv-hint" style={{ marginTop: 4 }}>{formatDistance(summary.totalMeters, unit)} driven · {plural(summary.tripCount, "trip")}</div>
            <div className="cv-figs-row">
              <div className="cv-fig"><div className="t">Deductible distance</div><div className="v">{formatDistance(summary.deductibleMeters, unit)}</div></div>
              {begunMonths > 1 && <div className="cv-fig"><div className="t">Average per month</div><div className="v">{formatMoney(summary.averageValuePerMonth, currency)}</div></div>}
              <div className="cv-fig"><div className="t">Classified trips</div><div className="v">{formatCount(classified)}</div></div>
            </div>
            {chartCategories.length > 0 && (
              <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
                <div className="cv-strip" role="img" aria-label={`Distance by category: ${listText(summary.categories.map((total) => `${total.category.name} ${formatDistance(total.meters, unit)}`))}`}>
                  {chartCategories.map((category) => {
                    const total = summary.categories.find((candidate) => candidate.category.rawValue === category.rawValue)!;
                    return <span key={category.rawValue} style={{ ...chartStyle(category), flex: total.meters }} />;
                  })}
                </div>
                <div className="cv-legend" aria-hidden="true">
                  {chartCategories.map((category) => <span key={category.rawValue}><i style={chartStyle(category)} />{category.name}</span>)}
                </div>
              </div>
            )}
          </section>

          <DistanceChart summary={summary} categories={chartCategories} unit={unit} interval={choice.interval} />

          <section className="cv-card" aria-labelledby="cat-title" style={{ overflow: "hidden" }}>
            <div className="cv-card-pad" style={{ paddingBottom: 8 }}><h2 id="cat-title" className="cv-section-title">By category</h2></div>
            {summary.categories.length === 0
              ? <p className="cv-hint" style={{ padding: "0 22px 18px" }}>No classified trips in this period.</p>
              : (
                <div className="cv-table-box">
                  <table className="cv-table">
                    <thead>
                      <tr>
                        <th scope="col" style={{ paddingLeft: 22 }}>Category</th>
                        <th scope="col" className="r">Trips</th>
                        <th scope="col" className="r">Distance</th>
                        <th scope="col" className="r">Rate</th>
                        <th scope="col" className="r" style={{ paddingRight: 22 }}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...summary.categories].sort((a, b) => (b.value ?? -1) - (a.value ?? -1) || a.category.listRank - b.category.listRank).map((total) => (
                        <tr key={total.category.rawValue}>
                          <th scope="row" style={{ paddingLeft: 22, fontWeight: 400, borderBottom: undefined }}><span className="cv-cat-cell"><CategoryBadge category={total.category} small />{total.category.name}</span></th>
                          <td className="r">{formatCount(total.tripCount)}</td>
                          <td className="r">{formatDistance(total.meters, unit)}</td>
                          <td className="r" style={total.rateRange ? undefined : { color: "var(--text-3)" }}>{rateText(total.rateRange, currency, total.rate?.unit ?? unit)}</td>
                          <td className="r" style={{ paddingRight: 22, color: total.value !== undefined ? "var(--pine)" : "var(--text-3)", fontWeight: total.value !== undefined ? 700 : 400 }}>
                            {total.value !== undefined ? formatMoney(total.value, currency) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td style={{ paddingLeft: 22 }}>Total</td>
                        <td className="r">{formatCount(classified)}</td>
                        <td className="r">{formatDistance(summary.totalMeters - summary.unclassifiedMeters, unit)}</td>
                        <td className="r" />
                        <td className="r" style={{ paddingRight: 22, color: "var(--pine)" }}>{formatMoney(summary.totalValue, currency)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            <p className="cv-hint" style={{ padding: "14px 22px 18px", borderTop: "1px solid var(--rule)" }}>
              Values use the flat rates in Mileage Rates. Check current official rates before you file; Carveny doesn’t give tax advice.
            </p>
          </section>
        </div>

        <aside className="cv-stack" style={{ flex: "1 1 320px", maxWidth: 400 }}>
          <RatesUsed interval={choice.interval} currency={currency} />
          <section className="cv-card cv-card-pad" aria-labelledby="exp-title">
            <h2 id="exp-title" className="cv-section-title" style={{ fontSize: 17 }}>Mileage log for this period</h2>
            <p className="cv-hint" style={{ marginTop: 6 }}>Every deductible trip with its date, times, places, purpose, rate and value.</p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
              <button className="cv-btn cv-btn-primary" type="button" onClick={() => downloadCSV(buildReport(data, logChoice))}><Icon name="download" />Download CSV</button>
              <button className="cv-btn cv-btn-secondary" type="button" onClick={() => openReport(logChoice)}><Icon name="print" />Printable report</button>
            </div>
          </section>
        </aside>
      </div>
    </>
  );
}

function rateText(range: [number, number] | undefined, currency: string, unit: DistanceUnit): string {
  if (!range) return "—";
  const [low, high] = range;
  if (low === high) return formatRate(low, currency, unit);
  return `${formatRate(low, currency, unit).replace(/\/.*$/, "")} – ${formatRate(high, currency, unit)}`;
}

/** The stored rates in force during the period, by category: "$0.725/mi from Jan 1, 2026 · …". */
function RatesUsed({ interval, currency }: { interval: Interval; currency: string }) {
  const data = useAccountData();
  const firstYear = interval.start.getFullYear();
  const lastYear = new Date(interval.end.getTime() - 1).getFullYear();
  const inRange = data.rates.all.filter((rate) => rate.year >= firstYear && rate.year <= lastYear);
  const years = [...new Set(data.rates.all.map((rate) => rate.year))].filter((year) => year <= lastYear).sort((a, b) => b - a);
  const shown = inRange.length > 0 ? inRange : data.rates.all.filter((rate) => rate.year === years[0]);
  const byCategory = new Map<string, { category: Category; text: string[] }>();
  for (const rate of [...shown].sort((a, b) => a.year - b.year || compareStarts(a.start, b.start))) {
    const category = data.catalog.resolve(rate.category);
    if (!category || rate.amount === null) continue;
    const entry = byCategory.get(category.rawValue) ?? { category, text: [] };
    entry.text.push(`${formatRate(rate.amount, rate.currencyCode || currency, rate.unit)} from ${formatShortDate(new Date(rate.year, rate.start.month - 1, rate.start.day))}`);
    byCategory.set(category.rawValue, entry);
  }
  const rows = [...byCategory.values()].sort((a, b) => a.category.listRank - b.category.listRank);

  return (
    <section className="cv-card cv-card-pad" aria-labelledby="rates-title">
      <h2 id="rates-title" className="cv-section-title" style={{ fontSize: 17 }}>Rates used</h2>
      {rows.length === 0
        ? <p className="cv-hint" style={{ marginTop: 10 }}>No mileage rates are set for this period.</p>
        : (
          <ul className="cv-list-plain" style={{ marginTop: 14, gap: 12 }}>
            {rows.map((row) => (
              <li key={row.category.rawValue} style={{ flexDirection: "column", gap: 2 }}>
                <b style={{ fontWeight: 600 }}>{row.category.name}</b>
                <span className="cv-hint num">{row.text.join(" · ")}</span>
              </li>
            ))}
          </ul>
        )}
      <p className="cv-hint" style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--rule)" }}>Change rates in the app: Trips › Trip Settings › Mileage Rates.</p>
    </section>
  );
}

function niceMax(value: number): number {
  if (value <= 0) return 4;
  const step = value / 4;
  const magnitude = 10 ** Math.floor(Math.log10(step));
  const nice = [1, 2, 2.5, 5, 10].find((candidate) => candidate * magnitude >= step) ?? 10;
  return nice * magnitude * 4;
}

/** Stacked bars of classified distance per month, or per day for a period of a month or less. */
function DistanceChart({ summary, categories, unit, interval }: { summary: MileageSummary; categories: Category[]; unit: DistanceUnit; interval: Interval }) {
  const byDay = summary.days.length > 0;
  const buckets: Bucket[] = byDay ? summary.days : summary.months;
  const max = niceMax(Math.max(0, ...buckets.map((bucket) => inUnit(bucketTotal(bucket), unit))));
  const ticks = [4, 3, 2, 1, 0].map((index) => (max / 4) * index);
  const label = (bucket: Bucket) => byDay
    ? bucket.start.getDate().toString()
    : bucket.start.toLocaleDateString(undefined, { month: "short" });
  const columns = { gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` };

  return (
    <section className="cv-card cv-card-pad" aria-labelledby="chart-title">
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 id="chart-title" className="cv-section-title">Distance by {byDay ? "day" : "month"}</h2>
        <span className="cv-hint" style={{ marginLeft: "auto" }}>Classified trips only</span>
      </div>
      <div className="cv-chart" role="img" aria-label={`Stacked bar chart of classified ${unit} per ${byDay ? "day" : "month"}, ${rangeText(interval)}. The By category table lists the totals.`}>
        <div className="cv-chart-axis" aria-hidden="true">
          {ticks.map((tick, index) => <span key={index}>{formatCount(Math.round(tick))}{index === ticks.length - 1 ? ` ${unitSymbol[unit]}` : ""}</span>)}
        </div>
        <div className={`cv-chart-plot${byDay ? " is-days" : ""}`} aria-hidden="true" style={columns}>
          {buckets.map((bucket) => {
            const total = inUnit(bucketTotal(bucket), unit);
            return (
              <div key={bucket.start.getTime()} className={`cv-bar${bucket.isPartial ? " is-partial" : ""}`} style={{ "--h": `${(total / max) * 100}%` } as React.CSSProperties}>
                {categories.map((category) => {
                  const meters = bucket.metersByCategory.get(category.rawValue) ?? 0;
                  if (meters <= 0) return null;
                  return <span key={category.rawValue} style={{ ...chartStyle(category), "--s": `${(inUnit(meters, unit) / total) * 100}%` } as React.CSSProperties} />;
                })}
              </div>
            );
          })}
        </div>
        <div className={`cv-chart-x${byDay ? " is-days" : ""}`} aria-hidden="true" style={columns}>
          {buckets.map((bucket) => <span key={bucket.start.getTime()}>{label(bucket)}</span>)}
        </div>
      </div>
    </section>
  );
}
